// ** React Imports
import { Link } from "react-router-dom";

// ** MUI Imports
import Box from "@mui/material/Box";
import ArrowLeft from "mdi-material-ui/ArrowLeft";
import AlertCircleOutline from "mdi-material-ui/AlertCircleOutline";

// ** Data / layout (Flix) + reused components/utils
import { useFlixCoursePlayer } from "src/flix/hooks/useFlixCoursePlayer";
import FlixPlayerLayout from "src/flix/layouts/FlixPlayerLayout";
import Spinner from "src/@core/components/spinner";
import { pathWithPreservedSearch } from "src/@core/utils/urlQueryPreservation";

/******************************************************************
 * ##: FlixPlayer
 * Netflix-style player page (Flix equivalent of /courses/...). All data
 * and progress logic come from useFlixCoursePlayer (which reuses the
 * shared API/utils); this page only maps loading/error/no-lessons to
 * neutral panels and hands the contract to FlixPlayerLayout.
 * @returns {JSX.Element} - the Flix player
 * History:
 * 22-07-2026: Created
 ******************************************************************/
const FlixPlayer = () => {
  const player = useFlixCoursePlayer();
  const { isLoading, errorLoadingData, noLessonsAvaliable, location } = player;

  if (isLoading) {
    // Reuse the app's branded loader (same component + message the real
    // player uses), not the alert panel — loading is not an error state.
    return <Spinner title="A carregar curso..." />;
  }

  if (errorLoadingData?.visible) {
    return <FlixPlayerStatus location={location} message={errorLoadingData.message} />;
  }

  if (noLessonsAvaliable) {
    return <FlixPlayerStatus location={location} message="Não existem aulas disponíveis para este curso." />;
  }

  return (
    <FlixPlayerLayout
      data={player.course}
      videos_info={player.videosInfos}
      actualChapter={player.actualChapter}
      actualLesson={player.actualLesson}
      lessonsStatus={player.lessonsStatus}
      isFirstLesson={player.isFirstLesson}
      isLastLesson={player.isLastLesson}
      moveToNextLesson={player.moveToNextLesson}
      moveToPreviousLesson={player.moveToPreviousLesson}
      updateVideoCurrentTime={player.updateVideoCurrentTime}
      updateAudioCurrentTime={player.updateAudioCurrentTime}
      lessonStatusHandler={player.lessonStatusHandler}
      activeLessonHandler={player.activeLessonHandler}
      refreshLessonsStatus={player.refreshLessonsStatus}
    />
  );
};

/******************************************************************
 * ##: FlixPlayerStatus
 * Neutral full-screen status panel (error / no lessons) with an optional
 * back link to the Flix catalog (token/query preserved). Loading is not
 * handled here — it reuses the app's <Spinner> (see FlixPlayer).
 * @param {Object} props - { location, message, showBack }
 * @returns {JSX.Element} - the `.flix-player__status`
 * History:
 * 22-07-2026: Created
 ******************************************************************/
const FlixPlayerStatus = ({ location, message, showBack = true }) => (
  <Box className="flix flix-player" sx={{ bgcolor: (theme) => (theme.palette.mode === "dark" ? "#141414" : "background.default"), color: "text.primary", minHeight: "100vh" }}>
    <div className="flix-player__status">
      <span className="flix-player__status-icon">
        <AlertCircleOutline />
      </span>
      <p className="flix-player__status-text">{message}</p>
      {showBack && (
        <Link className="flix-btn flix-btn--primary" to={pathWithPreservedSearch("/flix", location?.search || "")}>
          <ArrowLeft className="flix-btn__icon" />
          Voltar ao catálogo
        </Link>
      )}
    </div>
  </Box>
);

export default FlixPlayer;
